import React from "react";
import { App as AntdApp, Button, Card, DatePicker, Empty, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ClearOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import { useSSE } from "@modules/realtime/hooks/useSSE.js";
import { loadOrgTree } from "@modules/org/api/orgApi.js";
import { deletePromo, listPromos } from "../api/promoApi.js";
import CreatePromoModal from "../components/CreatePromoModal.jsx";
import "./PromosPage.css";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const fmt = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—");
const day = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

// Senza un limite esplicito il backend restituisce solo le 100 promo più recenti e la
// pagina nasconderebbe in silenzio le più vecchie, anche alla ricerca. Come nella pagina
// Volantini si carica l'insieme completo e ricerca/filtri lavorano lato client.
const PROMOS_FETCH_LIMIT = 2000;
const PAGE_SIZE = 20;

// Confronto senza maiuscole né accenti: "perù" trova "PERU".
const normalize = (v) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Una promo rientra nel periodo se il suo sellout lo tocca anche solo in parte. Le date
// si confrontano come giorni "YYYY-MM-DD" (l'ordine alfabetico coincide con quello
// cronologico); senza sellout si ripiega sulla data di avvio.
function matchesPeriod(promo, from, to) {
	const start = day(promo.sellOutFrom) || day(promo.startDate);
	const end = day(promo.sellOutTo) || day(promo.startDate);
	if (!start || !end) return false;
	if (from && end < from) return false;
	if (to && start > to) return false;
	return true;
}

export default function PromosPage() {
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const [searchParams, setSearchParams] = useSearchParams();
	const [promos, setPromos] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [openCreate, setOpenCreate] = React.useState(false);
	const [cooperative, setCooperative] = React.useState([]);
	const [page, setPage] = React.useState(1);

	// La query string è l'unica fonte di verità di ricerca e filtri: ricaricare la pagina,
	// tornare indietro dal dettaglio o incollare il link a un collega riproduce la stessa vista.
	const query = searchParams.get("q") || "";
	// getAll restituisce un array nuovo a ogni render: si memorizza sulla sua firma testuale.
	const clientKey = searchParams.getAll("cliente").join(",");
	const clientIds = React.useMemo(() => (clientKey ? clientKey.split(",") : []), [clientKey]);
	const periodFrom = searchParams.get("dal") || null;
	const periodTo = searchParams.get("al") || null;
	const hasFilters = !!query.trim() || clientIds.length > 0 || !!periodFrom || !!periodTo;

	const updateParams = (apply) => {
		setSearchParams((prev) => {
			const params = new URLSearchParams(prev);
			apply(params);
			return params;
		}, { replace: true });
		setPage(1);
	};

	const handleQueryChange = (value) => updateParams((params) => {
		if (value) params.set("q", value);
		else params.delete("q");
	});

	const handleClientsChange = (next) => updateParams((params) => {
		params.delete("cliente");
		next.forEach((id) => params.append("cliente", String(id)));
	});

	const handlePeriodChange = (range) => updateParams((params) => {
		const [from, to] = range || [];
		if (from) params.set("dal", from.format("YYYY-MM-DD"));
		else params.delete("dal");
		if (to) params.set("al", to.format("YYYY-MM-DD"));
		else params.delete("al");
	});

	const clearFilters = () => updateParams((params) => {
		["q", "cliente", "dal", "al"].forEach((key) => params.delete(key));
	});

	const fetchPromos = async () => {
		setLoading(true);
		try {
			setPromos(await listPromos({ limit: PROMOS_FETCH_LIMIT }));
		}
		catch (error) {
			message.error(error?.response?.data?.error || "Impossibile caricare le promo");
		}
		finally {
			setLoading(false);
		}
	};

	React.useEffect(() => { fetchPromos(); }, []);

	// Clienti dall'anagrafica, non dedotti dalle promo caricate: una cooperativa senza promo
	// resta selezionabile e dà un risultato vuoto esplicito (stessa scelta della pagina Volantini).
	React.useEffect(() => {
		let cancelled = false;
		loadOrgTree().then((items) => {
			if (cancelled) return;
			setCooperative(items
				.filter((n) => n.type === "COOPERATIVA")
				.map((n) => ({ value: String(n._id), label: n.name || "—" }))
				.sort((a, b) => a.label.localeCompare(b.label, "it")));
		});
		return () => { cancelled = true; };
	}, []);

	useSSE("promos", {
		"promo.created": (p) => setPromos((prev) => [p, ...prev.filter(x => (x._id || x.id) !== (p._id || p.id))]),
		"promo.updated": (p) => setPromos((prev) => prev.map(x => ((x._id || x.id) === (p._id || p.id) ? { ...x, ...p } : x))),
		"promo.deleted": (p) => setPromos((prev) => prev.filter(x => (x._id || x.id) !== p.id))
	});

	const visiblePromos = React.useMemo(() => {
		const needle = normalize(query.trim());
		const clients = new Set(clientIds);
		return promos.filter((p) => {
			if (needle) {
				const haystack = normalize([p.name, p.description, p.cooperativaName, p.consorzioName].join(" "));
				if (!haystack.includes(needle)) return false;
			}
			if (clients.size && !clients.has(String(p.cooperativaId || ""))) return false;
			if ((periodFrom || periodTo) && !matchesPeriod(p, periodFrom, periodTo)) return false;
			return true;
		});
	}, [promos, query, clientIds, periodFrom, periodTo]);

	const handleDelete = async (promo) => {
		try {
			const data = await deletePromo(promo._id || promo.id);
			const n = data?.cascadedFlyers ?? 0;
			message.success(n > 0 ? `Promo eliminata. ${n} volantini spostati nel cestino.` : "Promo eliminata.");
			setPromos((prev) => prev.filter(p => (p._id || p.id) !== (promo._id || promo.id)));
		}
		catch (error) {
			message.error(error?.response?.data?.error || "Errore eliminazione promo");
		}
	};

	const columns = [
		{ title: "Nome", dataIndex: "name", key: "name", render: (v, r) => <Space>{v}{r.isLegacy && <Tag>Legacy</Tag>}</Space> },
		{ title: "Consorzio", dataIndex: "consorzioName", key: "consorzio", render: (v) => v || "—" },
		{ title: "Cooperativa", dataIndex: "cooperativaName", key: "cooperativa", render: (v) => v || "—" },
		{ title: "Sellout", key: "sellout", render: (_, r) => `${fmt(r.sellOutFrom)} → ${fmt(r.sellOutTo)}` },
		{ title: "Data Avvio", dataIndex: "startDate", key: "startDate", render: fmt },
		{ title: "Volantini", dataIndex: "flyersCount", key: "flyersCount", render: (v) => <Tag color="blue">{v ?? 0}</Tag> },
		{
			title: "Azioni", key: "actions", width: 90, align: "center",
			render: (_, r) => r.isLegacy ? null : (
				<Popconfirm
					title="Eliminare la promo?"
					description={(r.flyersCount ?? 0) > 0
						? `${r.flyersCount} volantini verranno spostati nel cestino.`
						: "La promo verrà spostata nel cestino."}
					okText="Elimina"
					okButtonProps={{ danger: true }}
					cancelText="Annulla"
					onConfirm={() => handleDelete(r)}
					placement="left"
				>
					<Tooltip title="Elimina promo">
						<Button danger type="text" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
					</Tooltip>
				</Popconfirm>
			)
		}
	];

	return (
		<Card className="card-elev">
			<Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
				<Title level={4} style={{ margin: 0 }}>Promo</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenCreate(true)}>Nuova Promo</Button>
			</Space>

			<div className="promos-filters">
				<Input
					allowClear
					prefix={<SearchOutlined />}
					placeholder="Cerca per nome, cliente o consorzio"
					value={query}
					onChange={(e) => handleQueryChange(e.target.value)}
					style={{ width: 300 }}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					maxTagCount="responsive"
					placeholder="Cliente"
					value={clientIds}
					onChange={handleClientsChange}
					options={cooperative}
					style={{ minWidth: 240 }}
				/>
				<RangePicker
					allowEmpty={[true, true]}
					format="DD/MM/YYYY"
					placeholder={["Sellout dal", "al"]}
					value={[periodFrom ? dayjs(periodFrom) : null, periodTo ? dayjs(periodTo) : null]}
					onChange={handlePeriodChange}
				/>
				{hasFilters && (
					<Button icon={<ClearOutlined />} onClick={clearFilters}>Azzera filtri</Button>
				)}
			</div>

			<Table
				rowKey={(r) => r._id || r.id}
				columns={columns}
				dataSource={visiblePromos}
				loading={loading}
				pagination={{
					current: page,
					pageSize: PAGE_SIZE,
					onChange: setPage,
					showSizeChanger: false,
					showTotal: (total) => (hasFilters ? `${total} di ${promos.length} promo` : `${total} promo`)
				}}
				locale={{
					emptyText: hasFilters
						? (
							<Empty description="Nessuna promo corrisponde alla ricerca o ai filtri">
								<Button icon={<ClearOutlined />} onClick={clearFilters}>Azzera filtri</Button>
							</Empty>
						)
						: undefined
				}}
				onRow={(record) => ({
					style: { cursor: "pointer" },
					onClick: () => navigate(`/promos/${record._id || record.id}`, { state: { listSearch: searchParams.toString() } })
				})}
			/>

			<CreatePromoModal
				open={openCreate}
				onClose={() => setOpenCreate(false)}
				onPromoCreated={(promo) => {
					const id = promo?._id || promo?.id;
					if (id) navigate(`/promos/${id}`);
				}}
			/>
		</Card>
	);
}
